import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

// Lightweight searchable single-select combobox. Filters the items by
// `searchText` (which is the concatenation of every searchable field) so the
// user can type "+44", "uk", "united kingdom", or even "🇬🇧" — all match.
//
// items: [{ value, label, leading?, trailing?, searchText }]
// value: currently-selected `value`
// onValueChange: (newValue) => void
// renderTrigger: optional override to fully customise how the trigger button
// renders when an item is selected (we pass it the matched item).
export const SearchableCombobox = ({
  items,
  value,
  onValueChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No matches.",
  triggerTestId,
  popoverTestId,
  itemTestIdPrefix,
  renderTrigger,
  triggerClassName,
  buttonId,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const selected = items.find(i => i.value === value) || null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          id={buttonId}
          data-testid={triggerTestId}
          className={cn(
            "flex w-full items-center justify-between gap-2 h-11 rounded-xl border border-input bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
            triggerClassName
          )}
        >
          {selected ? (
            renderTrigger ? renderTrigger(selected) : (
              <span className="flex items-center gap-2 truncate">
                {selected.leading}
                <span className="truncate">{selected.label}</span>
              </span>
            )
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-[var(--radix-popover-trigger-width)] max-h-[60vh]"
        align="start"
        data-testid={popoverTestId}
      >
        <Command
          filter={(itemValue, search) => {
            // cmdk passes the item's `value` and the current search string. We
            // match against the item.searchText (lowercased) so labels and
            // dial codes both match.
            const it = items.find(i => i.value === itemValue);
            if (!it) return 0;
            const hay = (it.searchText || it.label || "").toLowerCase();
            return hay.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {items.map(item => (
                <CommandItem
                  key={item.value}
                  value={item.value}
                  data-testid={itemTestIdPrefix ? `${itemTestIdPrefix}-${item.value}` : undefined}
                  onSelect={(v) => {
                    onValueChange(v);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  {item.leading}
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.trailing && <span className="text-xs text-muted-foreground">{item.trailing}</span>}
                  <Check className={cn("w-4 h-4", value === item.value ? "opacity-100 text-primary" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
